import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatarCnpj } from '@via-tax/contracts'
import { Aviso, Botao, EstadoVazio, Selo } from '@via-tax/ui'
import { useCallback, useRef, useState, type DragEvent } from 'react'
import { SincronizacaoPortal } from '../components/SincronizacaoPortal'
import { dataBr, reais, ROTULO_MODELO } from '../formatos'
import type { ContextoSessao } from '../tipos'
import { mensagemDeErro, useTRPC, trpcClient } from '../trpc'
import { aceito, enviarEmPartes, formatarBytes } from '../upload'

interface ItemEnvio {
  arquivo: File
  nome: string
  enviados: number
  estado: 'fila' | 'enviando' | 'enviado' | 'erro'
  erro?: string
}

const CONCORRENCIA = 3

/** Lê pastas arrastadas (webkitGetAsEntry), recursivamente. */
async function arquivosDoArraste(e: DragEvent): Promise<File[]> {
  const entradas = [...e.dataTransfer.items].map((i) => i.webkitGetAsEntry?.()).filter(Boolean) as FileSystemEntry[]
  if (!entradas.length) return [...e.dataTransfer.files]
  const saida: File[] = []
  const visitar = async (en: FileSystemEntry, caminho: string): Promise<void> => {
    if (en.isFile) {
      const f = await new Promise<File>((ok, falha) => (en as FileSystemFileEntry).file(ok, falha))
      saida.push(new File([f], `${caminho}${f.name}`, { type: f.type }))
    } else if (en.isDirectory) {
      const leitor = (en as FileSystemDirectoryEntry).createReader()
      for (;;) {
        const lote = await new Promise<FileSystemEntry[]>((ok, falha) => leitor.readEntries(ok, falha))
        if (!lote.length) break
        for (const filho of lote) await visitar(filho, `${caminho}${en.name}/`)
      }
    }
  }
  for (const en of entradas) await visitar(en, '')
  return saida
}

export function EnviarDocumentos({ contexto }: { contexto: ContextoSessao }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const podeEnviar = (contexto.capacidades as string[]).includes('upload_xml')
  const [itens, setItens] = useState<ItemEnvio[]>([])
  const [loteId, setLoteId] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const [arrastando, setArrastando] = useState(false)
  const inputArquivos = useRef<HTMLInputElement>(null)
  const inputPasta = useRef<HTMLInputElement>(null)

  const status = useQuery({
    ...trpc.documento.statusLote.queryOptions({ loteId: loteId ?? '00000000-0000-0000-0000-000000000000' }),
    enabled: Boolean(loteId) && !enviando,
    refetchInterval: (q) => (q.state.data?.lote.status === 'concluido' ? false : 2000),
  })
  const historico = useQuery(trpc.documento.ultimosLotes.queryOptions())
  const rejeitados = useQuery({
    ...trpc.documento.rejeitados.queryOptions({ loteId: loteId ?? undefined }),
    enabled: Boolean(loteId) && status.data?.lote.status === 'concluido' && (status.data?.rejeitados ?? 0) > 0,
  })

  const adicionar = useCallback((arquivos: File[]) => {
    const validos = arquivos.filter(aceito)
    setItens((atual) => [
      ...atual,
      ...validos.map((arquivo) => ({ arquivo, nome: arquivo.webkitRelativePath || arquivo.name, enviados: 0, estado: 'fila' as const })),
    ])
    if (validos.length < arquivos.length) {
      setErroGeral(`${arquivos.length - validos.length} arquivo(s) ignorado(s): só XML e ZIP são aceitos.`)
    }
  }, [])

  const enviar = useMutation({
    mutationFn: async () => {
      setErroGeral(null)
      setEnviando(true)
      const { loteId: novo } = await trpcClient.documento.criarLote.mutate()
      setLoteId(novo)
      const atualizar = (i: number, p: Partial<ItemEnvio>) =>
        setItens((lista) => lista.map((it, j) => (j === i ? { ...it, ...p } : it)))

      let proximo = 0
      const trabalhador = async () => {
        while (proximo < itens.length) {
          const i = proximo++
          const it = itens[i]!
          atualizar(i, { estado: 'enviando' })
          try {
            const { arquivoId } = await trpcClient.documento.iniciarArquivo.mutate({ loteId: novo, nome: it.nome, tamanho: it.arquivo.size })
            await enviarEmPartes(arquivoId, it.arquivo, (n) => atualizar(i, { enviados: n }))
            await trpcClient.documento.concluirArquivo.mutate({ arquivoId })
            atualizar(i, { estado: 'enviado', enviados: it.arquivo.size })
          } catch (err) {
            atualizar(i, { estado: 'erro', erro: mensagemDeErro(err) })
          }
        }
      }
      await Promise.all(Array.from({ length: Math.min(CONCORRENCIA, itens.length) }, trabalhador))
      await trpcClient.documento.concluirEnvio.mutate({ loteId: novo })
    },
    onError: (err) => setErroGeral(mensagemDeErro(err)),
    onSettled: () => {
      setEnviando(false)
      void qc.invalidateQueries({ queryKey: trpc.documento.ultimosLotes.queryKey() })
    },
  })

  if (!podeEnviar) {
    return (
      <EstadoVazio titulo="Seu acesso é de leitura">
        Quem envia documentos é o escritório ou o administrador da empresa. Os documentos já recebidos estão em "Documentos recebidos".
      </EstadoVazio>
    )
  }

  const totalBytes = itens.reduce((a, i) => a + i.arquivo.size, 0)
  const enviadosBytes = itens.reduce((a, i) => a + i.enviados, 0)
  const concluido = status.data?.lote.status === 'concluido'
  const emProcessamento = loteId && !enviando && !concluido
  const s = status.data

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Enviar documentos fiscais</h1>
        <p className="mt-1 max-w-prose text-sm text-tinta-2">
          XML ou ZIP (pode ter pastas e ZIP dentro de ZIP) de NF-e, NFC-e, CT-e, NFS-e nacional e municipal, energia (NF3e)
          e telecom (NFCom), mais os eventos de cancelamento. Entrada e saída são identificadas pelo CNPJ — matriz e filiais
          juntas —, e documento de outra empresa é separado para você conferir.
        </p>
      </header>

      {!loteId && (
        <section
          onDragOver={(e) => {
            e.preventDefault()
            setArrastando(true)
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={async (e) => {
            e.preventDefault()
            setArrastando(false)
            adicionar(await arquivosDoArraste(e))
          }}
          className={`rounded-lg border-2 border-dashed px-6 py-10 text-center transition ${arrastando ? 'border-acao bg-acao/5' : 'border-linha bg-painel'}`}
        >
          <p className="text-sm font-semibold">Arraste arquivos ou pastas para cá</p>
          <p className="mt-1 text-xs text-tinta-3">Sem limite de quantidade. Cada arquivo até 2 GB; envio retoma se a conexão cair.</p>
          <div className="mt-4 flex justify-center gap-2">
            <Botao onClick={() => inputArquivos.current?.click()}>Escolher arquivos</Botao>
            <Botao onClick={() => inputPasta.current?.click()}>Escolher pasta</Botao>
          </div>
          <input
            ref={inputArquivos}
            type="file"
            multiple
            accept=".xml,.zip"
            hidden
            onChange={(e) => {
              adicionar([...(e.target.files ?? [])])
              e.target.value = ''
            }}
          />
          <input
            ref={inputPasta}
            type="file"
            hidden
            // @ts-expect-error atributo não padronizado, suportado pelos navegadores atuais
            webkitdirectory=""
            onChange={(e) => {
              adicionar([...(e.target.files ?? [])])
              e.target.value = ''
            }}
          />
        </section>
      )}

      {erroGeral && <Aviso>{erroGeral}</Aviso>}

      {itens.length > 0 && (
        <section className="rounded-lg border border-linha bg-painel">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-linha px-5 py-3">
            <div className="text-sm">
              <span className="font-semibold">{itens.length} arquivo(s)</span>
              <span className="text-tinta-3"> · {formatarBytes(totalBytes)}</span>
              {(enviando || loteId) && (
                <span className="num ml-2 text-tinta-2">{Math.round((enviadosBytes / Math.max(totalBytes, 1)) * 100)}% enviado</span>
              )}
            </div>
            {!loteId && (
              <div className="flex gap-2">
                <Botao variante="fantasma" onClick={() => setItens([])}>
                  Limpar
                </Botao>
                <Botao variante="primario" onClick={() => enviar.mutate()} disabled={enviar.isPending}>
                  Enviar {itens.length} arquivo(s)
                </Botao>
              </div>
            )}
          </div>
          <div className="h-1 bg-fundo" aria-hidden>
            <div className="h-1 bg-navy transition-all" style={{ width: `${(enviadosBytes / Math.max(totalBytes, 1)) * 100}%` }} />
          </div>
          <ul className="max-h-72 divide-y divide-linha overflow-y-auto text-[13px]">
            {itens.slice(0, 300).map((it, i) => (
              <li key={`${it.nome}-${i}`} className="flex items-center gap-3 px-5 py-2">
                <span className="min-w-0 flex-1 truncate" title={it.nome}>
                  {it.nome}
                </span>
                <span className="num shrink-0 text-xs text-tinta-3">{formatarBytes(it.arquivo.size)}</span>
                <span className="w-24 shrink-0 text-right text-xs">
                  {it.estado === 'fila' && <span className="text-tinta-3">na fila</span>}
                  {it.estado === 'enviando' && <span className="num text-navy">{Math.round((it.enviados / Math.max(it.arquivo.size, 1)) * 100)}%</span>}
                  {it.estado === 'enviado' && <span className="text-ok">enviado</span>}
                  {it.estado === 'erro' && <span className="text-alerta" title={it.erro}>falhou</span>}
                </span>
              </li>
            ))}
            {itens.length > 300 && <li className="px-5 py-2 text-xs text-tinta-3">… e mais {itens.length - 300} arquivo(s)</li>}
          </ul>
        </section>
      )}

      {loteId && s && (
        <section className="rounded-lg border border-linha bg-painel">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-linha px-5 py-3">
            <h2 className="text-sm font-semibold">Resultado do envio</h2>
            {concluido ? <Selo tom="ok">Processamento concluído</Selo> : <Selo tom="navy">{emProcessamento ? 'Lendo os documentos…' : 'Enviando…'}</Selo>}
          </div>
          <dl className="grid grid-cols-2 gap-px bg-linha sm:grid-cols-4">
            <Numero rotulo="Documentos novos" valor={s.documentosNovos} destaque />
            <Numero rotulo="Já existiam" valor={s.duplicados} nota="não duplicam" />
            <Numero rotulo="De outra empresa" valor={s.rejeitados} alerta={s.rejeitados > 0} />
            <Numero rotulo="Eventos" valor={s.eventos} nota="cancelamentos e CC-e" />
          </dl>
          {!concluido && (
            <p className="px-5 py-3 text-xs text-tinta-3">
              {s.arquivos.pendente ?? 0} arquivo(s) na fila de leitura. Pode sair desta tela: o processamento continua no servidor.
            </p>
          )}
          {s.problemas.length > 0 && (
            <div className="border-t border-linha px-5 py-3">
              <p className="text-xs font-semibold text-tinta-2">Arquivos não aproveitados</p>
              <ul className="mt-2 flex flex-col gap-1 text-xs">
                {s.problemas.map((p, i) => (
                  <li key={i} className="flex gap-2">
                    <span className={p.status === 'erro' ? 'text-alerta' : 'text-tinta-3'}>{p.status === 'erro' ? 'erro' : 'ignorado'}</span>
                    <span className="min-w-0 truncate text-tinta-2" title={p.nome}>
                      {p.nome}
                    </span>
                    <span className="text-tinta-3">— {p.mensagem}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {concluido && (
            <div className="flex gap-2 border-t border-linha px-5 py-3">
              <Botao
                variante="primario"
                onClick={() => {
                  setItens([])
                  setLoteId(null)
                }}
              >
                Enviar mais documentos
              </Botao>
            </div>
          )}
        </section>
      )}

      {rejeitados.data && rejeitados.data.length > 0 && (
        <section className="rounded-lg border border-alerta/30 bg-painel">
          <div className="border-b border-linha px-5 py-3">
            <h2 className="text-sm font-semibold">Documentos que não pertencem a esta empresa</h2>
            <p className="mt-0.5 text-xs text-tinta-3">
              Nenhum CNPJ com a raiz de {formatarCnpj(contexto.empresaAtiva?.cnpj)} aparece como emitente, destinatário ou
              tomador. Confira se o arquivo é de outra empresa.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-[13px]">
              <thead className="text-[11px] uppercase tracking-[0.06em] text-tinta-3">
                <tr className="border-b border-linha">
                  <th className="px-5 py-2 font-medium">Documento</th>
                  <th className="px-5 py-2 font-medium">Emissão</th>
                  <th className="px-5 py-2 font-medium">CNPJs encontrados</th>
                  <th className="px-5 py-2 text-right font-medium">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-linha">
                {rejeitados.data.map((r) => (
                  <tr key={r.id}>
                    <td className="px-5 py-2">
                      {ROTULO_MODELO[r.modelo] ?? r.modelo} {r.numero}
                      {r.motivo === 'NAO_TOMADOR' && <span className="ml-2 text-xs text-tinta-3">(a empresa não paga o frete)</span>}
                    </td>
                    <td className="num px-5 py-2">{dataBr(r.dataEmissao)}</td>
                    <td className="num px-5 py-2">{(r.cnpjsEncontrados as string[]).map((c) => formatarCnpj(c)).join(', ')}</td>
                    <td className="num px-5 py-2 text-right">{reais(r.valorTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!loteId && <SincronizacaoPortal contexto={contexto} />}

      {!loteId && (historico.data?.length ?? 0) > 0 && (
        <section className="rounded-lg border border-linha bg-painel">
          <h2 className="border-b border-linha px-5 py-3 text-sm font-semibold">Envios anteriores</h2>
          <ul className="divide-y divide-linha text-[13px]">
            {historico.data!.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-2.5">
                <span className="num w-24 text-tinta-2">{dataBr(l.criadoEm)}</span>
                {l.origem === 'PORTAL' && <Selo tom="navy">Portal</Selo>}
                <span>
                  <strong className="num">{l.documentosNovos}</strong> novo(s)
                </span>
                <span className="text-tinta-3">{l.duplicados} já existiam</span>
                {l.rejeitados > 0 && <span className="text-alerta">{l.rejeitados} de outra empresa</span>}
                <span className="ml-auto">{l.status === 'concluido' ? <Selo tom="ok">concluído</Selo> : <Selo tom="navy">{l.status}</Selo>}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

function Numero({ rotulo, valor, nota, destaque, alerta }: { rotulo: string; valor: number; nota?: string; destaque?: boolean; alerta?: boolean }) {
  return (
    <div className="bg-painel px-5 py-4">
      <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-tinta-3">{rotulo}</dt>
      <dd className={`num mt-1 text-2xl ${destaque ? 'text-navy' : alerta ? 'text-alerta' : ''}`}>{valor.toLocaleString('pt-BR')}</dd>
      {nota && <dd className="text-[11px] text-tinta-3">{nota}</dd>}
    </div>
  )
}
